import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrder } from '../../context/OrderContext';
import BracketSelector from '../../selectors/Accessori/BracketSelector';
import HandrailSelector from '../../selectors/Accessori/HandrailSelector';
import CoverStripSelector from '../../selectors/Accessori/CoverStripSelector';
import LedProfileSelector from '../../selectors/Accessori/LedProfileSelector';
import CapSelector from '../../selectors/Accessori/CapSelector';
import SpacerSelector from '../../selectors/Accessori/SpacerSelector';

const AccessoriesProcess = ({ accessoryType, onComplete, isEditMode = false, editItemIndex = null, profileType: propProfileType }) => {
    const { state, dispatch } = useOrder();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);

    // Use profileType from props (navigation state) or fall back to order state
    const profileType = propProfileType || state.orderDetails.profileType || state.orderDetails.ninfaNumber;

    // Initialize state based on current order details if in edit mode
    const [accessoryData, setAccessoryData] = useState(() => {
        if (isEditMode && editItemIndex !== null && state.cartItems && state.cartItems[editItemIndex]) {
            return state.cartItems[editItemIndex];
        }

        return {
            accessoryType,
            isAccessoryItem: true,
            // Default values for each accessory type
            giunzioni: {
                giunzioneType: '',
                giunzioneFinish: '',
                quantity: 1
            },
            handrails: {
                handrailType: '',
                glassSize: '',
                finish: '',
                length: 0,
                isCustomLength: false,
                quantity: 1
            },
            coverStrips: {
                coverStripType: '',
                quantity: 1
            },
            ledProfiles: {
                ledType: '',
                finish: '',
                length: 0,
                isCustomLength: false,
                quantity: 1
            },
            caps: {
                selected: false,
                quantityRight: 2,
                quantityLeft: 2
            },
            spacers: {
                spacerType: '',
                quantity: 1
            }
        };
    });

    // Get the cart item if in edit mode
    const [cartItem, setCartItem] = useState(null);

    useEffect(() => {
        if (isEditMode && editItemIndex !== null && state.cartItems && state.cartItems[editItemIndex]) {
            setCartItem(state.cartItems[editItemIndex]);
        }
    }, [isEditMode, editItemIndex, state.cartItems]);

    const handleBracketSelect = (giunzioneConfig) => {
        setAccessoryData(prev => ({
            ...prev,
            giunzioni: giunzioneConfig
        }));

        // Handle the new giunzione structure with multiple products
        if (giunzioneConfig.productCodes && giunzioneConfig.productCodes.length > 0) {
            // Add each giunzione product to cart individually
            giunzioneConfig.productCodes.forEach((product, index) => {
                const giunzioneArticle = {
                    productCode: product.code,
                    description: product.description,
                    quantity: product.quantity,
                    isAccessoryItem: true,
                    accessoryType: 'giunzioni',
                    category: product.category, // 'main'
                    finish: giunzioneConfig.finish,
                    ralCode: giunzioneConfig.ralCode || product.ralCode || null, // RAL code for V1/V2 finishes
                    giunzioneType: giunzioneConfig.giunzioneType,
                    giunzioneFinish: giunzioneConfig.giunzioneFinish,
                    // Store wizard steps for configuration tracking
                    wizardSteps: [
                        `Tipo: ${giunzioneConfig.giunzioneType}`,
                        `Finitura: ${giunzioneConfig.finish}`,
                        ...(giunzioneConfig.ralCode ? [`Codice RAL: ${giunzioneConfig.ralCode}`] : []),
                        `Quantità: ${product.quantity}`
                    ]
                };

                // Add the article to the cart using the dispatch function
                if (isEditMode && editItemIndex !== null && index === 0) {
                    // Update the first item if editing
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: { index: editItemIndex, item: giunzioneArticle }
                    });
                } else {
                    // Add new items to cart
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: giunzioneArticle
                    });
                }
            });
        }

        // Notify parent component that the process is complete
        if (onComplete) {
            onComplete(giunzioneConfig);
        } else {
            // Navigate to order summary only if no onComplete callback
            setTimeout(() => {
                navigate('/order-summary');
            }, 500); // Small delay to ensure cart items are added
        }
    };

    const handleHandrailSelect = (handrailConfig) => {
        setAccessoryData(prev => ({
            ...prev,
            handrails: handrailConfig
        }));

        // Handle the new handrail structure with multiple products
        if (handrailConfig.productCodes && handrailConfig.productCodes.length > 0) {
            // Add each handrail product to cart individually
            handrailConfig.productCodes.forEach((product, index) => {
                const handrailArticle = {
                    productCode: product.code,
                    description: product.description,
                    quantity: product.quantity,
                    customBars: handrailConfig.customBars, // Add custom bars for "misura"
                    supplyType: handrailConfig.supplyType, // Add supply type
                    isAccessoryItem: true,
                    accessoryType: 'corrimano',
                    category: product.category, // 'main' or 'automatic'
                    finish: handrailConfig.finish,
                    ralCode: handrailConfig.ralCode || product.ralCode || null, // RAL code for V1/V2 finishes
                    glassThickness: handrailConfig.glassThickness,
                    selectedHandrails: handrailConfig.selectedHandrails,
                    // Store wizard steps for configuration tracking
                    wizardSteps: [
                        `Profilo: ${handrailConfig.profileType ? `NINFA ${handrailConfig.profileType}` : 'Universal'}`,
                        `Tipo: ${product.description.split(' - ')[0]}`,
                        `Fornitura: ${handrailConfig.supplyType === '3m' ? 'Barre da 3 Metri' :
                                      handrailConfig.supplyType === '6m' ? 'Barre da 6 Metri' :
                                      'Tagliato a Misura'}`,
                        `Finitura: ${handrailConfig.finish}`,
                        ...(handrailConfig.ralCode ? [`Codice RAL: ${handrailConfig.ralCode}`] : []),
                        ...(handrailConfig.glassThickness ? [`Vetro: ${handrailConfig.glassThickness}`] : []),
                        `Quantità: ${product.quantity}${product.category === 'automatic' ? ' (Auto)' : ''}`
                    ]
                };

                // Add the article to the cart using the dispatch function
                if (isEditMode && editItemIndex !== null && index === 0) {
                    // Update the first item if editing
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: { index: editItemIndex, item: handrailArticle }
                    });
                } else {
                    // Add new items to cart
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: handrailArticle
                    });
                }
            });
        } else {
            // Fallback for old handrail structure (legacy support)
            if (handrailConfig.handrailType) {
                // Determine length type for product code
                let lengthType = 'BARRE';
                if (handrailConfig.isCustomLength) {
                    lengthType = 'MISURA';
                } else if (handrailConfig.length === 3000) {
                    lengthType = 'BARRE3M';
                } else if (handrailConfig.length === 6000) {
                    lengthType = 'BARRE6M';
                }

                // Create the article
                const handrailArticle = {
                    productCode: `ACC-CR-${handrailConfig.handrailType === 'standard' ? 'STD' : 'CRV'}-${handrailConfig.finish || 'N'}-${lengthType}`,
                    description: `Corrimano ${handrailConfig.handrailType === 'standard' ? 'Standard' : 'Curvo'} - ${handrailConfig.finish === 'N' ? 'Nero' :
                        handrailConfig.finish === 'B' ? 'Bianco' :
                            handrailConfig.finish === 'A' ? 'Anodizzato' :
                                handrailConfig.finish === 'G' ? 'Grezzo' : 'RAL'
                        } - Vetro: ${handrailConfig.glassSize || 'N/A'} - ${handrailConfig.isCustomLength ? `Su misura: ${handrailConfig.length}mm` :
                            handrailConfig.length === 3000 ? 'Barra 3 metri' :
                                handrailConfig.length === 6000 ? 'Barra 6 metri' : 'Lunghezza standard'
                        }`,
                    quantity: handrailConfig.quantity || 1,
                    isAccessoryItem: true,
                    accessoryType: 'corrimano',
                    handrails: {
                        ...handrailConfig,
                        type: handrailConfig.handrailType
                    }
                };

                // Add article to cart
                if (isEditMode && editItemIndex !== null) {
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: {
                            index: editItemIndex,
                            item: handrailArticle
                        }
                    });
                } else {
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: handrailArticle
                    });
                }
            }
        }

        // Notify parent component that the process is complete
        if (onComplete) {
            onComplete(handrailConfig);
        } else {
            // Navigate to order summary only if no onComplete callback
            setTimeout(() => {
                navigate('/order-summary');
            }, 500); // Small delay to ensure cart items are added
        }
    };

    const handleCoverStripSelect = (coverStripConfig) => {
        setAccessoryData(prev => ({
            ...prev,
            coverStrips: coverStripConfig
        }));

        // Handle the new coverstrip structure with multiple products
        if (coverStripConfig.productCodes && coverStripConfig.productCodes.length > 0) {
            // Add each coverstrip product to cart individually
            coverStripConfig.productCodes.forEach((product, index) => {
                const coverStripArticle = {
                    productCode: product.code,
                    description: product.description,
                    quantity: product.quantity,
                    profile: product.profile,
                    glassOption: product.glassOption,
                    customBars: coverStripConfig.customBars, // Add custom bars for "misura" and angolare
                    isAccessoryItem: true,
                    accessoryType: 'coprifilo',
                    finish: coverStripConfig.finish,
                    ralCode: coverStripConfig.ralCode || product.ralCode || null, // RAL code for V1/V2 finishes
                    selectedCoverStrips: coverStripConfig.selectedCoverStrips,
                    supplyType: coverStripConfig.supplyType,
                    // Store wizard steps for configuration tracking
                    wizardSteps: [
                        `Profilo: ${coverStripConfig.profileType ? `NINFA ${coverStripConfig.profileType}` : `NINFA ${product.profile}`}`,
                        `Vetro: ${product.glassOption}`,
                        `Tipo: ${product.type === 'coprifilo' ? 'Coprifilo' : 'Angolare'}`,
                        `Fornitura: ${coverStripConfig.supplyType === '3m' ? 'Barre da 3 Metri' : coverStripConfig.supplyType === '6m' ? 'Barre da 6 Metri' : 'Tagliato a Misura'}`,
                        `Finitura: ${coverStripConfig.finish}`,
                        ...(coverStripConfig.ralCode ? [`Codice RAL: ${coverStripConfig.ralCode}`] : []),
                        `Quantità: ${product.quantity}`
                    ]
                };

                // Add the article to the cart using the dispatch function
                if (isEditMode && editItemIndex !== null && index === 0) {
                    // Update the first item if editing
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: { index: editItemIndex, item: coverStripArticle }
                    });
                } else {
                    // Add new items to cart
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: coverStripArticle
                    });
                }
            });
        } else {
            // Fallback for old coverstrip structure (legacy support)
            if (coverStripConfig.coverStripType) {
                let productCode, description;

                switch (coverStripConfig.coverStripType) {
                    case 'lShaped':
                        productCode = 'ACC-CP-L';
                        description = 'Angolare a L';
                        break;
                    case 'channelType1':
                        productCode = 'ACC-CP-C1';
                        description = 'Coprifilo Tipo 1';
                        break;
                    case 'channelType2':
                        productCode = 'ACC-CP-C2';
                        description = 'Coprifilo Tipo 2';
                        break;
                    default:
                        productCode = 'ACC-CP-UNK';
                        description = 'Coprifilo Sconosciuto';
                }

                const coverStripArticle = {
                    productCode,
                    description,
                    quantity: coverStripConfig.quantity || 1,
                    isAccessoryItem: true,
                    accessoryType: 'coprifilo',
                    coverStrips: {
                        ...coverStripConfig,
                        type: coverStripConfig.coverStripType
                    }
                };

                // Add article to cart
                if (isEditMode && editItemIndex !== null) {
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: {
                            index: editItemIndex,
                            item: coverStripArticle
                        }
                    });
                } else {
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: coverStripArticle
                    });
                }
            }
        }

        // Notify parent component that the process is complete
        if (onComplete) {
            onComplete(coverStripConfig);
        } else {
            // Navigate to order summary only if no onComplete callback
            setTimeout(() => {
                navigate('/order-summary');
            }, 500); // Small delay to ensure cart items are added
        }
    };

    const handleLedProfileSelect = (ledProfileConfig) => {
        setAccessoryData(prev => ({
            ...prev,
            ledProfiles: ledProfileConfig
        }));

        // Handle the new LED profile structure with multiple products (aluminum + plastic)
        if (ledProfileConfig.productCodes && ledProfileConfig.productCodes.length > 0) {
            // Add each LED product to cart individually
            ledProfileConfig.productCodes.forEach((product, index) => {
                const ledArticle = {
                    productCode: product.code,
                    description: product.description,
                    quantity: product.quantity,
                    length: product.length, // Add length from product
                    customBars: ledProfileConfig.customBars, // Add custom bars for "misura"
                    isAccessoryItem: true,
                    accessoryType: 'led',
                    category: product.category, // 'main' or 'automatic'
                    finish: ledProfileConfig.finish,
                    ralCode: ledProfileConfig.ralCode || product.ralCode || null, // RAL code for V1/V2 finishes
                    supplyType: ledProfileConfig.supplyType,
                    selectedProfile: ledProfileConfig.selectedProfile,
                    // Store wizard steps for configuration tracking
                    wizardSteps: [
                        `Tipo: ${ledProfileConfig.selectedProfile?.name || 'Profilo Segnapassi'}`,
                        `Fornitura: ${ledProfileConfig.supplyType === '3m' ? 'Barre da 3 Metri (3000mm)' :
                                      ledProfileConfig.supplyType === '6m' ? 'Barre da 6 Metri (6000mm)' :
                                      'Tagliato a Misura'}`,
                        `Finitura: ${ledProfileConfig.finish}`,
                        ...(ledProfileConfig.ralCode ? [`Codice RAL: ${ledProfileConfig.ralCode}`] : []),
                        `Quantità: ${product.quantity}${product.category === 'automatic' ? ' (Auto)' : ''}`
                    ]
                };

                // Add the article to the cart using the dispatch function
                if (isEditMode && editItemIndex !== null && index === 0) {
                    // Update the first item if editing
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: { index: editItemIndex, item: ledArticle }
                    });
                } else {
                    // Add new items to cart
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: ledArticle
                    });
                }
            });
        } else {
            // Fallback for old LED profile structure (legacy support)
            if (ledProfileConfig.ledType) {
                // Determine length type for product code
                let lengthType = 'BARRE';
                if (ledProfileConfig.isCustomLength) {
                    lengthType = 'MISURA';
                } else if (ledProfileConfig.length === 3000) {
                    lengthType = 'BARRE3M';
                } else if (ledProfileConfig.length === 6000) {
                    lengthType = 'BARRE6M';
                }

                // Create the article
                const ledArticle = {
                    productCode: `ACC-LED-${ledProfileConfig.finish || 'N'}-${lengthType}`,
                    description: `Profilo LED - ${ledProfileConfig.finish === 'N' ? 'Nero' :
                        ledProfileConfig.finish === 'B' ? 'Bianco' :
                            ledProfileConfig.finish === 'A' ? 'Anodizzato' :
                                ledProfileConfig.finish === 'G' ? 'Grezzo' : 'RAL'
                        } - ${ledProfileConfig.isCustomLength ? `Su misura: ${ledProfileConfig.length}mm` :
                            ledProfileConfig.length === 3000 ? 'Barra 3 metri' :
                                ledProfileConfig.length === 6000 ? 'Barra 6 metri' : 'Lunghezza standard'
                        }`,
                    quantity: ledProfileConfig.quantity || 1,
                    isAccessoryItem: true,
                    accessoryType: 'led',
                    ledProfiles: ledProfileConfig
                };

                if (isEditMode && editItemIndex !== null) {
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: {
                            index: editItemIndex,
                            item: ledArticle
                        }
                    });
                } else {
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: ledArticle
                    });
                }
            }
        }

        // Notify parent component that the process is complete
        if (onComplete) {
            onComplete(ledProfileConfig);
        } else {
            // Navigate to order summary only if no onComplete callback
            setTimeout(() => {
                navigate('/order-summary');
            }, 500); // Small delay to ensure cart items are added
        }
    };

    const handleCapSelect = (capConfig) => {
        setAccessoryData(prev => ({
            ...prev,
            caps: capConfig
        }));

        // Handle the new cap structure with multiple products
        if (capConfig.productCodes && capConfig.productCodes.length > 0) {
            // Add each cap product to cart individually
            capConfig.productCodes.forEach((product, index) => {
                const capArticle = {
                    productCode: product.code,
                    description: product.description,
                    quantity: product.quantity,
                    profile: product.profile,
                    isAccessoryItem: true,
                    accessoryType: 'tappi',
                    finish: capConfig.finish,
                    ralCode: capConfig.ralCode || product.ralCode || null, // RAL code for V1/V2 finishes
                    selectedCaps: capConfig.selectedCaps,
                    // Store wizard steps for configuration tracking
                    wizardSteps: [
                        `Profilo: ${capConfig.profileType ? `NINFA ${capConfig.profileType}` : `NINFA ${product.profile}`}`,
                        `Tipo: ${product.description.split(' - ')[0]}`,
                        `Finitura: ${capConfig.finish}`,
                        ...(capConfig.ralCode ? [`Codice RAL: ${capConfig.ralCode}`] : []),
                        `Quantità: ${product.quantity}`
                    ]
                };

                // Add the article to the cart using the dispatch function
                if (isEditMode && editItemIndex !== null && index === 0) {
                    // Update the first item if editing
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: { index: editItemIndex, item: capArticle }
                    });
                } else {
                    // Add new items to cart
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: capArticle
                    });
                }
            });
        } else {
            // Fallback for old cap structure (legacy support)
            if (capConfig.selected) {
                const capArticle = {
                    productCode: 'ACC-TP-NINFA',
                    description: 'Tappi NINFA',
                    quantityRight: capConfig.quantityRight || 0,
                    quantityLeft: capConfig.quantityLeft || 0,
                    quantity: (capConfig.quantityRight || 0) + (capConfig.quantityLeft || 0),
                    isAccessoryItem: true,
                    accessoryType: 'tappi',
                    caps: capConfig
                };

                if (isEditMode && editItemIndex !== null) {
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: {
                            index: editItemIndex,
                            item: capArticle
                        }
                    });
                } else {
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: capArticle
                    });
                }
            }
        }

        // Notify parent component that the process is complete
        if (onComplete) {
            onComplete(capConfig);
        } else {
            // Navigate to order summary only if no onComplete callback
            setTimeout(() => {
                navigate('/order-summary');
            }, 500); // Small delay to ensure cart items are added
        }
    };

    const handleSpacerSelect = (spacerConfig) => {
        setAccessoryData(prev => ({
            ...prev,
            spacers: spacerConfig
        }));

        // Handle the new spacer structure with multiple products
        if (spacerConfig.productCodes && spacerConfig.productCodes.length > 0) {
            // Add each spacer product to cart individually
            spacerConfig.productCodes.forEach((product, index) => {
                const spacerArticle = {
                    productCode: product.code,
                    description: product.description,
                    quantity: product.quantity,
                    profile: product.profile,
                    isAccessoryItem: true,
                    accessoryType: 'spessori',
                    finish: spacerConfig.finish,
                    ralCode: spacerConfig.ralCode || product.ralCode || null, // RAL code for V1/V2 finishes
                    selectedSpacers: spacerConfig.selectedSpacers,
                    // Store wizard steps for configuration tracking
                    wizardSteps: [
                        `Profilo: ${spacerConfig.profileType ? `NINFA ${spacerConfig.profileType}` : `NINFA ${product.profile}`}`,
                        `Tipo: ${product.description.split(' - ')[0]}`,
                        `Finitura: ${spacerConfig.finish || 'Argento (Fisso)'}`,
                        ...(spacerConfig.ralCode ? [`Codice RAL: ${spacerConfig.ralCode}`] : []),
                        `Quantità: ${product.quantity}`
                    ]
                };

                // Add the article to the cart using the dispatch function
                if (isEditMode && editItemIndex !== null && index === 0) {
                    // Update the first item if editing
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: { index: editItemIndex, item: spacerArticle }
                    });
                } else {
                    // Add new items to cart
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: spacerArticle
                    });
                }
            });
        } else {
            // Fallback for old spacer structure (legacy support)
            if (spacerConfig.spacerType) {
                const spacerArticle = {
                    productCode: 'ACC-SP-ACQUA',
                    description: 'Spessori Acqua',
                    quantity: spacerConfig.quantity || 1,
                    isAccessoryItem: true,
                    accessoryType: 'spessori',
                    spacers: spacerConfig
                };

                if (isEditMode && editItemIndex !== null) {
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: {
                            index: editItemIndex,
                            item: spacerArticle
                        }
                    });
                } else {
                    dispatch({
                        type: 'ADD_ACCESSORY_TO_CART',
                        payload: spacerArticle
                    });
                }
            }
        }

        // Notify parent component that the process is complete
        if (onComplete) {
            onComplete(spacerConfig);
        } else {
            // Navigate to order summary only if no onComplete callback
            setTimeout(() => {
                navigate('/order-summary');
            }, 500); // Small delay to ensure cart items are added
        }
    };


    // Render the appropriate selector based on accessory type
    const renderSelector = () => {
        switch (accessoryType) {
            case 'giunzioni':
                return (
                    <BracketSelector
                        onSelect={handleBracketSelect}
                        initialValues={cartItem?.giunzioni || cartItem || {}}
                        isEditMode={isEditMode}
                        profileType={profileType}
                    />
                );
            case 'corrimano':
                return (
                    <HandrailSelector
                        onSelect={handleHandrailSelect}
                        initialValues={cartItem?.handrails || {}}
                        isEditMode={isEditMode}
                        profileType={profileType}
                    />
                );
            case 'coprifilo':
                return (
                    <CoverStripSelector
                        onSelect={handleCoverStripSelect}
                        initialValues={cartItem?.coverStrips || {}}
                        isEditMode={isEditMode}
                        profileType={profileType}
                    />
                );
            case 'led':
                return (
                    <LedProfileSelector
                        onSelect={handleLedProfileSelect}
                        initialValues={cartItem?.ledProfiles || {}}
                        isEditMode={isEditMode}
                        profileType={profileType}
                    />
                );
            case 'tappi':
                return (
                    <CapSelector
                        onSelect={handleCapSelect}
                        initialValues={cartItem?.caps || {}}
                        isEditMode={isEditMode}
                        profileType={profileType}
                    />
                );
            case 'spessori':
                return (
                    <SpacerSelector
                        onSelect={handleSpacerSelect}
                        initialValues={cartItem?.spacers || {}}
                        isEditMode={isEditMode}
                        profileType={profileType}
                    />
                );
            default:
                return <div>Selettore non disponibile per questo tipo di accessorio</div>;
        }
    };

    return (
        <div className="container mx-auto">
            <h2 className="text-xl font-bold mb-6 text-center" style={{ textTransform: 'capitalize' }}>
                {isEditMode ? `Modifica ${accessoryType}` : `${accessoryType}`}
            </h2>
            {renderSelector()}
        </div>
    );
};

export default AccessoriesProcess;
